import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SaveIcon from '@mui/icons-material/Save';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useZoom } from 'context/ZoomContext';
import { MODEL_FILTER_TYPES } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { useFilterPopover } from 'dashboard409a/hooks/useFilterPopover';
import { useModelFilters } from 'dashboard409a/hooks/useModelFilters';
import { ModelFiltersProps } from './filters.props';
import { GroupedUserAutocomplete } from './GroupedUserAutocomplete';
import { modelFiltersStyles } from './ModelFilters.styles';
import { ModelView } from '../ModelViewsSelector/ModelViewsSelector.props';

export const ModelFilters = ({
  statusOptions,
  statusOptionsLoading,
  onFiltersChange,
  viewToApply,
  views = [],
  currentViewId,
  onViewChange,
  onSaveView,
  onDeleteView,
  onEditView,
}: ModelFiltersProps) => {
  const { handleOpen, popoverProps } = useFilterPopover({
    popoverId: 'model-filters-popover',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    minHeight: '120px',
  });

  const { zoomLevel } = useZoom();
  const [viewNameDialogOpen, setViewNameDialogOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const isLoadingViewRef = useRef(false);
  const {
    selectedStatuses,
    filterType,
    filterTypeSelection,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    statusSearchInput,
    sortedStatuses,
    isOnlyUnassignedSelected,
    handleStatusesChange,
    handleFilterTypeToggle,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange,
    handleReviewersChange,
    setPreparerSearchInput,
    setReviewerSearchInput,
    setStatusSearchInput,
    cleanFilters,
  } = useModelFilters({
    onFiltersChange,
    statusOptions,
    viewToApply: viewToApply
      ? {
        preparerIds: viewToApply.preparerIds,
        reviewerIds: viewToApply.reviewerIds,
        searchStatus: viewToApply.searchStatus,
        fromDate: viewToApply.fromDate,
        toDate: viewToApply.toDate,
        filterType: viewToApply.filterType,
        filterTypeSelection: viewToApply.filterTypeSelection,
      }
      : null,
  });

  const currentView = views.find(v => v.id === currentViewId) || null;
  const isDefaultView = currentViewId === 'default' || !currentViewId;

  // Add "All" option at the beginning of status options
  const statusesWithAll = useMemo(() => {
    const allOption = { value: 'All', label: 'All' };
    return [allOption, ...sortedStatuses];
  }, [sortedStatuses]);

  // Check if all individual statuses are selected
  const allStatusesSelected = useMemo(
    () => sortedStatuses.length > 0 &&
      sortedStatuses.every(status => selectedStatuses.some(s => s.value === status.value)),
    [sortedStatuses, selectedStatuses]
  );

  // Handle status selection with "Select All" logic
  const handleStatusSelectAll = useCallback(
    (_event: unknown, newValue: typeof selectedStatuses) => {
      const isAllInNewValue = newValue.some(s => s.value === 'All');

      if (isAllInNewValue && !allStatusesSelected) {
        // "All" was clicked when not all were selected - select all options
        handleStatusesChange(_event, [...sortedStatuses]);
      } else if (isAllInNewValue && allStatusesSelected) {
        // "All" was clicked when all were already selected - deselect all
        handleStatusesChange(_event, []);
      } else {
        // Normal individual option selection
        // Filter out "All" from the value since it should only be shown visually when all are selected
        const valueWithoutAll = newValue.filter(s => s.value !== 'All');
        handleStatusesChange(_event, valueWithoutAll);
      }
    },
    [allStatusesSelected, sortedStatuses, handleStatusesChange]
  );

  // Render option for status autocomplete with "All" checkbox logic
  const renderStatusOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: { value: string; label: string }, { selected }: { selected: boolean }) => {
      const { key, ...optionProps } = props;
      // For "All", show as selected if all options are selected
      const isAllOption = option.value === 'All';
      const isChecked = isAllOption ? allStatusesSelected : selected;

      return (
        <li key={option.value} {...optionProps} style={modelFiltersStyles.optionListItem}>
          <Checkbox style={modelFiltersStyles.optionCheckbox} checked={isChecked} />
          {option.label}
        </li>
      );
    },
    [allStatusesSelected]
  );


  // Prevent auto-save while a view is being loaded
  useEffect(() => {
    if (viewToApply) {
      isLoadingViewRef.current = true;
      const timer = setTimeout(() => {
        isLoadingViewRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewToApply?.id]);

  const handleEditCurrentView = useCallback(() => {
    if (currentView && currentView.id !== 'default') {
      setViewName(currentView.name);
      setEditingViewId(currentView.id);
      setViewNameDialogOpen(true);
    }
  }, [currentView]);

  const handleDeleteCurrentView = useCallback(() => {
    if (currentViewId && currentViewId !== 'default' && onDeleteView) {
      onDeleteView(currentViewId);
      if (onViewChange) {
        onViewChange(null);
      }
    }
  }, [currentViewId, onDeleteView, onViewChange]);

  const handleCreateNewView = useCallback(() => {
    setViewName('');
    setEditingViewId(null);
    setViewNameDialogOpen(true);
  }, []);

  const handleSaveViewName = useCallback(() => {
    if (viewName.trim()) {
      if (editingViewId && onEditView) {
        const viewToEdit = views.find(v => v.id === editingViewId);
        if (viewToEdit) {
          const preparerIdsArray = selectedPreparers.map(p => p.id).filter((id): id is number => id !== undefined);
          const reviewerIdsArray = selectedReviewers.map(r => r.id).filter((id): id is number => id !== undefined);
          const searchStatusValues = selectedStatuses.map(s => s.value);
          const updatedView: ModelView = {
            ...viewToEdit,
            name: viewName.trim(),
            preparerIds: preparerIdsArray,
            reviewerIds: reviewerIdsArray,
            searchStatus: searchStatusValues,
            fromDate: fromDueDate,
            toDate: toDueDate,
            filterType,
            filterTypeSelection,
          };
          onEditView(updatedView);
        }
      } else if (onSaveView) {
        // onSaveView will create the view with current filters
        onSaveView(viewName.trim());
      }
      setViewNameDialogOpen(false);
      setViewName('');
      setEditingViewId(null);
    }
  }, [viewName, editingViewId, onEditView, onSaveView, views, selectedPreparers, selectedReviewers, selectedStatuses, fromDueDate, toDueDate, filterType, filterTypeSelection]);

  const handleClearFilters = useCallback(() => {
    cleanFilters();
    // Also clear the current view if it's not default
    if (currentViewId && currentViewId !== 'default' && onEditView && currentView) {
      const clearedView: ModelView = {
        ...currentView,
        preparerIds: [],
        reviewerIds: [],
        searchStatus: [],
        fromDate: null,
        toDate: null,
        filterType: MODEL_FILTER_TYPES.CURRENT,
        filterTypeSelection: { current: true, past: false, unassigned: false, all: false },
      };
      onEditView(clearedView);
    }
  }, [cleanFilters, currentViewId, currentView, onEditView]);

  const handleViewSelect = useCallback(
    (viewId: string | null) => {
      if (onViewChange) {
        if (viewId === 'default' || !viewId) {
          onViewChange(null);
        } else {
          const selectedView = views.find(v => v.id === viewId);
          if (selectedView) {
            onViewChange(selectedView);
          }
        }
      }
    },
    [onViewChange, views]
  );

  return (
    <>
      <Tooltip title="Filters" arrow>
        <IconButton onClick={handleOpen}>
          <FilterAltIcon />
        </IconButton>
      </Tooltip>
      <Popover {...popoverProps}>
        <Box sx={{ p: 3, minWidth: 750 }}>
          {/* Header with Filter View dropdown */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight="medium">
                Filter View:
              </Typography>
              <Select
                size="small"
                value={currentViewId || 'default'}
                onChange={e => handleViewSelect(e.target.value)}
                sx={{ minWidth: 200 }}
                renderValue={value => {
                  if (value === 'default') {
                    return 'Default';
                  }
                  const view = views.find(v => v.id === value);
                  return view?.name || 'Default';
                }}>
                <MenuItem value="default">Default</MenuItem>
                {views
                  .filter(view => view.id !== 'default')
                  .map(view => (
                    <MenuItem key={view.id} value={view.id}>
                      {view.name}
                    </MenuItem>
                  ))}
              </Select>
              <Tooltip title="Save View">
                <IconButton size="small" onClick={handleEditCurrentView} disabled={!currentView || isDefaultView}>
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete View">
                <IconButton size="small" onClick={handleDeleteCurrentView} disabled={isDefaultView}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                onClick={handleCreateNewView}
                sx={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                +NEW
              </Button>
            </Box>
            <IconButton size="small" onClick={() => popoverProps.onClose?.({}, 'backdropClick')}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Filter Type Checkboxes Row */}
          <Box display="flex" alignItems="center" mb={3} sx={{ width: '100%', userSelect: 'none' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', mr: 2 }}>Filter by</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection.current}
                  onChange={handleFilterTypeToggle('current')}
                />
              }
              label="Current"
              sx={{ flex: 1 }}
              onDoubleClick={e => e.preventDefault()}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection.past}
                  onChange={handleFilterTypeToggle('past')}
                />
              }
              label="Past"
              sx={{ flex: 1 }}
              onDoubleClick={e => e.preventDefault()}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection.unassigned}
                  onChange={handleFilterTypeToggle('unassigned')}
                />
              }
              label="Unassigned"
              sx={{ flex: 1 }}
              onDoubleClick={e => e.preventDefault()}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection.all}
                  onChange={handleFilterTypeToggle('all')}
                />
              }
              label="All"
              sx={{ flex: 1 }}
              onDoubleClick={e => e.preventDefault()}
            />
          </Box>

          {/* 3 Column Layout */}
          <Grid container spacing={3} mb={3}>
            {/* Column 1: Select Status */}
            <Grid size={{sm: 4}}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Autocomplete
                  size="small"
                  multiple
                  options={statusesWithAll}
                  getOptionLabel={option => option.label}
                  value={selectedStatuses}
                  onChange={handleStatusSelectAll}
                  onInputChange={(event, newInputValue) => {
                    setStatusSearchInput(newInputValue);
                  }}
                  inputValue={statusSearchInput}
                  loading={statusOptionsLoading}
                  disableCloseOnSelect
                  limitTags={-1}
                  slotProps={{
                    popper: {
                      sx: modelFiltersStyles.getAutocompletePopper(zoomLevel),
                    },
                  }}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="Status"
                      variant="outlined" 
                      placeholder={selectedStatuses.length === 0 ? 'Select Status' : ''}
                      sx={modelFiltersStyles.autocompleteTextField}
                    />
                  )}
                  renderTags={tagValue => {
                    if (tagValue.length === 0) {
                      return null;
                    }
                    
                    const maxVisible = 2;
                    const visibleItems = tagValue.slice(0, maxVisible);
                    const remainingCount = tagValue.length - maxVisible;
                    
                    return (
                      <Box sx={modelFiltersStyles.getTagsContainer(tagValue.length)}>
                        {visibleItems.map(option => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            size="small"
                            sx={modelFiltersStyles.chip}
                          />
                        ))}
                        {remainingCount > 0 && (
                          <Chip
                            label={`+${remainingCount}`}
                            size="small"
                            sx={modelFiltersStyles.chip}
                          />
                        )}
                      </Box>
                    );
                  }}
                  renderOption={renderStatusOption}
                  sx={modelFiltersStyles.autocompleteRoot}
                  isOptionEqualToValue={(option, val) => option.value === val.value}
                />
              </Box>
            </Grid>

            {/* Column 2: From and To */}
            <Grid size={{sm: 4}}>
              <Box display="flex" flexDirection="column" gap={2}>
                <DatePicker
                  label="From"
                  value={fromDueDate}
                  onChange={handleFromDueDateChange}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="To"
                  value={toDueDate}
                  onChange={handleToDueDateChange}
                  minDate={fromDueDate || undefined}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Column 3: Select Reviewers and Select Preparers */}
            <Grid size={{sm: 4}}>
              <Box display="flex" flexDirection="column" gap={2}>
                <GroupedUserAutocomplete
                  value={selectedReviewers}
                  onChange={handleReviewersChange}
                  onInputChange={setReviewerSearchInput}
                  inputValue={reviewerSearchInput}
                  label="Select Reviewers"
                  placeholder="Search for reviewers..."
                  zoomLevel={zoomLevel}
                  textFieldSx={modelFiltersStyles.autocompleteTextField}
                  disabled={isOnlyUnassignedSelected}
                />
                <GroupedUserAutocomplete
                  value={selectedPreparers}
                  onChange={handlePreparersChange}
                  onInputChange={setPreparerSearchInput}
                  inputValue={preparerSearchInput}
                  label="Select Preparers"
                  placeholder="Search for preparers..."
                  zoomLevel={zoomLevel}
                  textFieldSx={modelFiltersStyles.preparersTextField}
                  disabled={isOnlyUnassignedSelected}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Footer with Clear Filters button */}
          <Box display="flex" justifyContent="flex-end" mt={3} pt={2} borderTop="1px solid #e0e0e0">
            <Button variant="outlined" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Dialog for saving/editing view name */}
      <Dialog
        open={viewNameDialogOpen}
        onClose={() => {
          setViewNameDialogOpen(false);
          setViewName('');
          setEditingViewId(null);
        }}>
        <DialogTitle>{editingViewId ? 'Edit View' : 'Create New View'}</DialogTitle>
        <DialogContent>
          <TextField
            label="View Name"
            value={viewName}
            onChange={e => setViewName(e.target.value)}
            fullWidth
            autoFocus
            margin="normal"
            onKeyDown={e => {
              if (e.key === 'Enter' && viewName.trim()) {
                e.preventDefault();
                handleSaveViewName();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewNameDialogOpen(false);
              setViewName('');
              setEditingViewId(null);
            }}>
            Cancel
          </Button>
          <Button onClick={handleSaveViewName} variant="contained" disabled={!viewName.trim()}>
            {editingViewId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
